"use client";

import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import ButtonSignin from "./ButtonSignin";
import logo from "@/app/icon.png";
import config from "@/config";
import logo2 from "/public/logos/main_logo.svg";
import { createBrowserClient } from "@/utils/supabase/client";
import { User } from "@supabase/supabase-js";
import { FiHome, FiPieChart, FiDollarSign, FiSettings } from "react-icons/fi";

interface LinkItem {
  href: string;
  label: string;
}

const links: LinkItem[] = [
  {
    href: "/#pricing",
    label: "Pricing",
  },
  {
    href: "/#features",
    label: "Features",
  },
  {
    href: "/#faq",
    label: "FAQ",
  },
];

const cta = <ButtonSignin extraStyle='' />;

const Header: React.FC = () => {
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const supabase = createBrowserClient();

  useEffect(() => {
    setIsOpen(false);
  }, [searchParams]);

  useEffect(() => {
    const getUser = async () => {
      const { data } = await supabase.auth.getUser();
      setUser(data.user);
    };

    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [supabase]);

  return (
    <header className='bg-black'>
      <nav
        className='container flex items-center justify-between px-8 py-4 mx-auto'
        aria-label='Global'
      >
        <div className='flex lg:flex-1'>
          <Link
            className='flex items-center gap-2 shrink-0 '
            href='/'
            title={`${config.appName} hompage`}
          >
            <Image
              src={logo2}
              alt={`${config.appName} logo2`}
              className='w-8'
              priority={true}
              width={32}
              height={32}
            />
            <span className='font-extrabold text-lg text-white'>
              {config.appName}
            </span>
          </Link>
        </div>
        <div className='flex lg:hidden'>
          <button
            type='button'
            className='inline-flex items-center justify-center rounded-md p-3 min-h-[44px] min-w-[44px] active:scale-95 transition-transform'
            onClick={() => setIsOpen(true)}
          >
            <span className='sr-only'>Open main menu</span>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth={1.5}
              stroke='currentColor'
              className='w-6 h-6 text-white'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5'
              />
            </svg>
          </button>
        </div>

        <div className='text-white hidden lg:flex lg:justify-center lg:gap-12 lg:items-center'>
          {links.map((link) => (
            <Link
              href={link.href}
              key={link.href}
              className='link link-hover'
              title={link.label}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className='hidden lg:flex lg:justify-end lg:flex-1'>{cta}</div>
      </nav>

      <div className={`relative z-50 ${isOpen ? "" : "hidden"}`}>
        <div
          className={`fixed inset-y-0 right-0 z-10 w-full px-8 py-4 overflow-y-auto bg-base-200 sm:max-w-sm sm:ring-1 sm:ring-neutral/10 transform origin-right transition ease-in-out duration-300`}
        >
          <div className='flex items-center justify-between'>
            <Link
              className='flex items-center gap-2 shrink-0 '
              title={`${config.appName} hompage`}
              href='/'
            >
              <Image
                src={logo}
                alt={`${config.appName} logo`}
                className='w-8'
                placeholder='blur'
                priority={true}
                width={32}
                height={32}
              />
              <span className='font-extrabold text-lg'>{config.appName}</span>
            </Link>
            <button
              type='button'
              className='rounded-md p-3 min-h-[44px] min-w-[44px] active:scale-95 transition-transform'
              onClick={() => setIsOpen(false)}
            >
              <span className='sr-only'>Close menu</span>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='w-6 h-6'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>

          {/* Mobile menu content */}
          <div className='flow-root mt-6'>
            {/* User info section if authenticated */}
            {user && (
              <div className='mb-6 p-4 bg-base-300 rounded-lg'>
                <div className='flex items-center gap-3'>
                  {user.user_metadata?.avatar_url ? (
                    <img
                      src={user.user_metadata.avatar_url}
                      alt={user.user_metadata?.name || user.email}
                      className='w-12 h-12 rounded-full'
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className='w-12 h-12 bg-bb-secondary text-ink-strong flex justify-center items-center rounded-full font-semibold text-lg'>
                      {(user.user_metadata?.name || user.email || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className='font-medium text-base-content'>
                      {user.user_metadata?.name || user.email?.split('@')[0]}
                    </p>
                    <p className='text-sm text-base-content/60'>{user.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick actions for authenticated users */}
            {user && (
              <div className='mb-6'>
                <p className='text-sm font-medium text-base-content/60 mb-3'>Quick Actions</p>
                <div className='grid grid-cols-2 gap-3'>
                  <Link
                    href='/dashboard'
                    className='flex flex-col items-center gap-2 p-4 bg-base-300 rounded-lg hover:bg-base-300/80 transition-colors'
                    onClick={() => setIsOpen(false)}
                  >
                    <FiHome className='w-6 h-6 text-bb-primary' />
                    <span className='text-sm font-medium'>Dashboard</span>
                  </Link>
                  <Link
                    href='/budget'
                    className='flex flex-col items-center gap-2 p-4 bg-base-300 rounded-lg hover:bg-base-300/80 transition-colors'
                    onClick={() => setIsOpen(false)}
                  >
                    <FiPieChart className='w-6 h-6 text-bb-primary' />
                    <span className='text-sm font-medium'>Budget</span>
                  </Link>
                  <Link
                    href='/transactions/new'
                    className='flex flex-col items-center gap-2 p-4 bg-base-300 rounded-lg hover:bg-base-300/80 transition-colors'
                    onClick={() => setIsOpen(false)}
                  >
                    <FiDollarSign className='w-6 h-6 text-bb-primary' />
                    <span className='text-sm font-medium'>Add Transaction</span>
                  </Link>
                  <Link
                    href='/settings'
                    className='flex flex-col items-center gap-2 p-4 bg-base-300 rounded-lg hover:bg-base-300/80 transition-colors'
                    onClick={() => setIsOpen(false)}
                  >
                    <FiSettings className='w-6 h-6 text-bb-primary' />
                    <span className='text-sm font-medium'>Settings</span>
                  </Link>
                </div>
              </div>
            )}

            <div className='py-4'>
              <div className='flex flex-col gap-y-4 items-start'>
                {links.map((link) => (
                  <Link
                    href={link.href}
                    key={link.href}
                    className='link link-hover py-2'
                    title={link.label}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div className='divider'></div>
            <div className='flex flex-col'>{cta}</div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
